// apps/client/src/hooks/useMonthlySettlements.ts
// MIGRATED (2025-11-26): Now uses BigQuery via getMonthlySettlements Cloud Function
// Previous: Queried Firestore companies/{companyId}/monthly_settlements collection (deprecated)
// Current: Queries BigQuery monthly_settlement_aggregates + raw_commissions via Cloud Function
// MIGRATED (2026-07-10): Fetches through the shared dashboard query cache (useDashboardQuery).

import { useMemo } from 'react';
import { useDashboardQuery } from './useDashboardQuery';

interface MonthlySettlement {
  yearMonth: string;
  companyId: string;
  totalSettled: number;        // Sum in cents (loan amounts, not commissions)
  settlementCount: number;
  positiveSettled: number;
  clawbacks: number;
  totalTopUps: number;
  topUpCount: number;
  updatedAt: any;
  byTemplate?: Record<string, {
    total: number;
    count: number;
    byLender?: Record<string, {
      total: number;
      count: number;
    }>;
  }>;
  byOffice?: Record<string, {
    total: number;
    count: number;
    byTemplate?: Record<string, {
      total: number;
      count: number;
      byLender?: Record<string, {
        total: number;
        count: number;
      }>;
      byBroker: Record<string, {
        brokerDisplay: string;
        total: number;
        count: number;
        avgLoanSize: number;
      }>;
    }>;
    byBroker: Record<string, {
      brokerDisplay: string;
      total: number;
      count: number;
      avgLoanSize: number;
    }>;
  }>;
  byLender?: Record<string, { total: number; count: number }>;
  byBroker?: Record<string, {
    brokerDisplay: string;
    total: number;
    count: number;
    avgLoanSize: number;
  }>;
}

interface UseMonthlySettlementsParams {
  companyId: string;
  // Option A: Year-based (existing, backward compatible)
  year?: number;
  yearType?: 'calendar' | 'financial';
  // Option B: Month-range based (new - for relative periods)
  startMonth?: string;  // YYYY-MM format
  endMonth?: string;    // YYYY-MM format
  // Filters
  officeId?: string;
  templateId?: string;
  brokerId?: string;
  entityCode?: string;
}

// Cloud Function response type (matches getMonthlySettlements.ts)
interface MonthlySettlementsResponse {
  months: Array<{
    month: string;                      // 'YYYY-MM'
    totalLoanAmountCents: number;       // Loan amounts (not commissions)
    settlementCount: number;
    totalCommissionCents: number;
    clawbackCount: number;
    clawbackTotalCents: number;
    byBroker?: Record<string, {
      brokerDisplay: string;
      totalLoanAmountCents: number;
      settlementCount: number;
      totalCommissionCents: number;
    }>;
    byLender?: Record<string, {
      totalLoanAmountCents: number;
      settlementCount: number;
    }>;
    byTemplate?: Record<string, {
      totalLoanAmountCents: number;
      settlementCount: number;
    }>;
  }>;
  totals: {
    totalLoanAmount: number;
    totalCommission: number;
    totalCount: number;
    uniqueSettlementCount?: number;
    uniqueSettledCents?: number;
    uniqueByYear?: Record<string, { count: number; cents: number }>;
  };
}

/**
 * Hook for fetching monthly settlement aggregates
 * Returns "Total Loans Settled" data for dashboard chart
 *
 * MIGRATED (2025-11-26): Now uses BigQuery via getMonthlySettlements Cloud Function
 * Previous: Queried Firestore monthly_settlements collection (deprecated, no longer populated)
 */
export function useMonthlySettlements({ companyId, year, yearType, startMonth, endMonth, officeId, templateId, brokerId, entityCode }: UseMonthlySettlementsParams) {
  const { data, loading, error } = useDashboardQuery<MonthlySettlementsResponse>(
    'getMonthlySettlements',
    {
      companyId,
      ...(startMonth && endMonth
        ? { startMonth, endMonth }
        : { year, yearType }),
      officeId,
      templateId,
      brokerId,
      entityCode,
    }
  );

  // Transform Cloud Function response to match existing MonthlySettlement interface
  const settlements = useMemo<MonthlySettlement[]>(() => {
    if (!data) return [];

    return data.months.map((item) => ({
      yearMonth: item.month,
      companyId,
      totalSettled: item.totalLoanAmountCents,  // Loan amounts in cents
      settlementCount: item.settlementCount,
      positiveSettled: item.totalLoanAmountCents > 0 ? item.totalLoanAmountCents : 0,
      clawbacks: item.clawbackTotalCents,
      totalTopUps: 0,
      topUpCount: 0,
      updatedAt: null,
      // Transform byBroker breakdown
      byBroker: item.byBroker ? Object.fromEntries(
        Object.entries(item.byBroker).map(([brokerId, brokerData]) => [
          brokerId,
          {
            brokerDisplay: brokerData.brokerDisplay,
            total: brokerData.totalLoanAmountCents,
            count: brokerData.settlementCount,
            avgLoanSize: brokerData.settlementCount > 0
              ? Math.round(brokerData.totalLoanAmountCents / brokerData.settlementCount)
              : 0,
          },
        ])
      ) : undefined,
      // Transform byLender breakdown
      byLender: item.byLender ? Object.fromEntries(
        Object.entries(item.byLender).map(([lender, lenderData]) => [
          lender,
          { total: lenderData.totalLoanAmountCents, count: lenderData.settlementCount },
        ])
      ) : undefined,
      // Transform byTemplate breakdown
      byTemplate: item.byTemplate ? Object.fromEntries(
        Object.entries(item.byTemplate).map(([templateId, templateData]) => [
          templateId,
          { total: templateData.totalLoanAmountCents, count: templateData.settlementCount },
        ])
      ) : undefined,
    }));
  }, [data, companyId]);

  // Extract deduplicated unique totals from backend (fallback to summed values for backward compat)
  const uniqueTotalSettled = data ? (data.totals.uniqueSettledCents ?? data.totals.totalLoanAmount) : 0;
  const uniqueTotalCount = data ? (data.totals.uniqueSettlementCount ?? data.totals.totalCount) : 0;
  const uniqueByYear = data ? data.totals.uniqueByYear : undefined;

  // Calculate totals for the year
  const totalSettled = settlements.reduce((sum, s) => sum + (s.totalSettled || 0), 0);
  const totalCount = settlements.reduce((sum, s) => sum + (s.settlementCount || 0), 0);

  return {
    settlements,
    loading,
    error,
    totalSettled,
    totalCount,
    uniqueTotalSettled,
    uniqueTotalCount,
    uniqueByYear,
  };
}
